import {
  type AccordionGroup,
  MyNaviFilterAccordionList,
} from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterAccordionList'
import { MyNaviFilterLayout } from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterLayout'
import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type Area = { name: string; prefectures: string[] }

type Props = {
  initialPrefectures: string[]
  onSelect: (prefectures: string[]) => void
  onClear: () => void
  onClose: () => void
  areas?: Area[]
}

export function FilterArea({ initialPrefectures, onSelect, onClear, onClose, areas: areasProp }: Props) {
  const api = useApi()

  const [localPrefectures, setLocalPrefectures] = useState<Set<string>>(() => new Set(initialPrefectures))

  const { data: areasData } = useQuery({
    queryKey: ['mynavi-areas'],
    queryFn: () => api.guest.fetchMynaviAreas(false),
    staleTime: 5 * 60 * 1000,
    enabled: areasProp === undefined,
  })

  const { data: countData } = useQuery({
    queryKey: ['mynavi-filter-count', 'area', [...localPrefectures].sort().join(',')],
    queryFn: () =>
      api.guest.fetchMynaviCollegesFilterCount({
        prefectures: localPrefectures.size > 0 ? [...localPrefectures].sort() : undefined,
      }),
    staleTime: 30 * 1000,
    enabled: areasProp === undefined,
  })

  const resolvedAreas: Area[] = areasProp ?? areasData?.areas ?? []

  const groups: AccordionGroup[] = resolvedAreas.map((area) => {
    const allChecked = area.prefectures.every((p) => localPrefectures.has(p))

    const onToggleAll = () => {
      setLocalPrefectures((prev) => {
        const next = new Set(prev)
        if (allChecked) {
          area.prefectures.forEach((p) => next.delete(p))
        } else {
          area.prefectures.forEach((p) => next.add(p))
        }
        return next
      })
    }

    return {
      name: area.name,
      allChecked,
      onToggleAll,
      items: area.prefectures.map((pref) => ({
        id: pref,
        label: pref,
        checked: localPrefectures.has(pref),
        onToggle: () => {
          setLocalPrefectures((prev) => {
            const next = new Set(prev)
            if (next.has(pref)) {
              next.delete(pref)
            } else {
              next.add(pref)
            }
            return next
          })
        },
      })),
    }
  })

  const handleSelect = () => {
    onSelect([...localPrefectures].sort())
  }

  const handleClear = () => {
    setLocalPrefectures(new Set())
    onClear()
  }

  return (
    <MyNaviFilterLayout
      title="エリアを選択する"
      onClose={onClose}
      searchResultCount={countData?.count ?? null}
      onSelect={handleSelect}
      onClear={handleClear}
    >
      <MyNaviFilterAccordionList groups={groups} />
    </MyNaviFilterLayout>
  )
}
